import {
  Card,
  Grid,
  Paper,
  Typography,
  withStyles,
  Switch,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import React, { Component } from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { connect } from "react-redux";
import { appService } from "../../App/app.service";
import AddHeaderDescriptionWithHelperText from "../../components/HelperTextDescription";
import DataTable from "../ProductsByAccount/DataTable";
import { ResizableTable } from "../../components/resizableTable/ResizableTable";
import { countOrACVFormatter } from "../../util/customFunctions";

const styles = (theme) => ({
  root: {},
  content: {},
  gridSection: {
    marginTop: "1rem",
  },
  kpiRow: {
    marginBottom: "1rem",
    alignItems: "stretch",
  },
  kpiCard: {
    padding: "1.5rem",
    height: "100%",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  // Style for the Toggle Card (Disabled State)
  toggleCard: {
    padding: "1.5rem",
    height: "100%",
    borderRadius: 12,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    background: "#F0F9FF",
    border: "1px solid #BAE6FD",
    boxShadow: "none",
  },
  toggleLabel: {
    fontWeight: 600,
    fontSize: "1.1rem",
    marginLeft: "1rem",
    color: "#0F172A",
  },
  kpiNeutral: {
    background: "#F5F6F8",
    color: "#111827",
    border: "1px solid #E5E7EB",
  },
  kpiPrimary: {
    background: "linear-gradient(90deg, #1E88E5 0%, #1976D2 100%)",
    color: "#ffffff",
  },
  title: {
    fontWeight: 600,
    minHeight: "3.5rem",
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "0.5rem",
    [theme.breakpoints.down("sm")]: {
      minHeight: "2.8rem",
    },
  },
  secondaryTitle: {
    color: "#607C8B",
  },
  metricPrimary: {
    fontWeight: 700,
    fontSize: "2.4rem",
    lineHeight: 1,
    display: "block",
    [theme.breakpoints.up("md")]: {
      fontSize: "3.4rem",
    },
  },
  metricSecondary: {
    fontWeight: 700,
    marginTop: "0.25rem",
    fontSize: "1.6rem",
    [theme.breakpoints.up("md")]: {
      fontSize: "2rem",
    },
  },
  upliftLabel: {
    fontWeight: 700,
    fontSize: "1.25rem",
    marginLeft: "1rem",
    opacity: 0.9,
    [theme.breakpoints.up("md")]: {
      fontSize: "1.5rem",
    },
  },
  metricRow: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
  },
  smallKpiCard: {
    padding: "1rem",
    borderRadius: 12,
    boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
  },
  sectionTitle: {
    width: "100%",
    margin: "auto",
    padding: "1rem",
    paddingTop: "0.3rem",
    fontWeight: 600,
  },
  toggleGroup: {
    borderBottom: "1px solid #e5e7eb",
    "& .MuiToggleButtonGroup-grouped": {
      border: "none",
      margin: "0 12px",
    },
  },
  toggleBtn: {
    position: "relative",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "1rem",
    color: "#6B7280",
    padding: "0.75rem 1rem",
    border: "none !important",
    borderRadius: 0,
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
    "&.Mui-selected": {
      color: "#1E88E5",
      backgroundColor: "transparent",
    },
    "&.Mui-selected:after": {
      content: '""',
      display: "block",
      height: 3,
      background: "#1E88E5",
      position: "absolute",
      left: 10,
      right: 10,
      bottom: -1,
      borderRadius: 2,
    },
    // Style for disabled tabs
    "&.Mui-disabled": {
      color: "#D1D5DB",
      cursor: "not-allowed",
    },
  },
  // Blur class for hidden content
  blurredContent: {
    filter: "blur(6px)",
    pointerEvents: "none",
    userSelect: "none",
    opacity: 0.7,
    transition: "all 0.3s ease",
  },
  // Normal class for visible content
  normalContent: {
    filter: "blur(0px)",
    opacity: 1,
    transition: "all 0.3s ease",
  },
});

const transformToResizableFromSimpleColumns = (
  simpleColumns,
  data,
  currencySymbol
) => {
  const columns = [
    {
      label: "#",
      subColumns: [
        {
          label: "#",
          field: "_index",
          type: "number",
          align: "center",
          minWidth: 48,
          width: 48,
        },
      ],
      headerBgColor: "#4471c4",
      headerTextColor: "#ffffff",
    },
    ...simpleColumns.map((col, idx) => {
      const align = col.type === "string" ? "left" : "right";
      const typeMap =
        col.type === "currency"
          ? "currency"
          : col.type === "integer"
          ? "number"
          : col.type === "date"
          ? "date"
          : "text";
      // const defaultMinWidth =
      //   col.type === "string" ? 160 : col.type === "date" ? 140 : 120;
      return {
        label: col.label,
        sortable: true,
        subColumns: [
          {
            label: col.label,
            field: col.field,
            type: typeMap,
            align,
            // minWidth: defaultMinWidth,
            sortable: true,
            filterable: true,
          },
        ],
        headerBgColor: idx % 2 === 0 ? "#5b9bd5" : "#4471c4",
        headerTextColor: "#ffffff",
      };
    }),
  ];

  const rows =
    (data || []).map((row, idx) => ({
      ...row,
      _index: idx + 1,
    })) || [];

  return { columns, rows };
};

const createGetCellValue = (simpleColumns) => (row, field, rowIndex) => {
  if (field === "_index") {
    // Return current display position (1-based) - always shows 1, 2, 3, 4, 5... in order
    // rowIndex is 0-based, so add 1 to make it 1-based
    return rowIndex !== undefined ? rowIndex + 1 : row._index || 1;
  }
  const col = simpleColumns?.find((c) => c.field === field);
  if (!col) return row[field] ?? "";
  const value = row[field];
  if (col.type === "currency") {
    return value != null ? countOrACVFormatter(value, true) : "";
  }
  if (col.type === "integer") {
    return value != null ? countOrACVFormatter(value, false) : "";
  }
  return value ?? "";
};

const getCellStyleCommon = (row) => {
  const style = {};
  if (row && row.bg) {
    style.backgroundColor = row.bg;
  }
  if (row && row.clr) {
    style.color = row.clr;
  }
  return style;
};

class WhiteSpaceConversionEngine extends Component {
  state = {
    tab: "summary",
    isSkyGeniRecommendationEnabled: false, // Default is disabled
  };

  componentDidMount = () => {
    this.props.getRankOrderedRecommendationsData();
    this.props.getSummaryRecommendationsData();
    this.props.getAccountWiseRecommendationsData();
  };

  handleChangeTab = (e, newValue) => {
    // newValue is null if clicking active tab, check needed
    if (newValue) {
      this.setState({
        ...this.state,
        tab: newValue,
      });
    }
  };

  handleToggleChange = (event) => {
    this.setState({
      isSkyGeniRecommendationEnabled: event.target.checked,
    });
  };

  render() {
    const { classes } = this.props;
    const { isSkyGeniRecommendationEnabled } = this.state;

    // Fetch basePipeline from props, default to 0 if loading
    const basePipeline = Number(
      (this.props.SummaryRecommendationsData &&
        this.props.SummaryRecommendationsData.currentBasePipeline) ||
        0
    );

    // Fetch sumListPriceUpdated from props, default to 0 if loading
    const sumListPriceUpdated = Number(
      (this.props.SummaryRecommendationsData &&
        this.props.SummaryRecommendationsData.sumListPriceUpdated) ||
        0
    );

    // Calculate uplift percent and incremental hidden expansion
    const upliftPercent =
      basePipeline > 0 ? (sumListPriceUpdated / basePipeline) * 100 : 0;
    const incrementalHiddenExpansion = sumListPriceUpdated - basePipeline;

    // Format numbers in millions with Currency_Symbol
    const formatMillions = (value) => {
      if (value === null || value === undefined || isNaN(Number(value)))
        return `${this.props.Currency_Symbol}0`;
      const num = Number(value);
      const abs = Math.abs(num);
      if (abs >= 1e6)
        return `${this.props.Currency_Symbol}${(num / 1e6).toFixed(1)}M`;
      if (abs >= 1e3)
        return `${this.props.Currency_Symbol}${(num / 1e3).toFixed(1)}K`;
      return `${this.props.Currency_Symbol}${Math.round(num).toLocaleString()}`;
    };

    // Format percent with rounding
    const formatPercent = (value) => `${Math.round(value)}%`;

    return (
      <Grid container>
        <AddHeaderDescriptionWithHelperText
          header={`White Space Conversion Engine`}
          description={`SkyGeni Recommendations of upsell and cross-sell product groups.`}
          helperTextCharts={this.props.helperTextCharts}
        />

        <Grid
          item
          xs={12}
          md={12}
          id="grid-container"
          className={classes.gridSection}
        >
          <Paper style={{ marginBottom: "-7px" }}>
            <ToggleButtonGroup
              color="primary"
              value={this.state.tab}
              exclusive
              size="medium"
              onChange={this.handleChangeTab}
              className={classes.toggleGroup}
            >
              <ToggleButton className={classes.toggleBtn} value="summary">
                Summary
              </ToggleButton>
              <ToggleButton
                className={classes.toggleBtn}
                value="accountWise"
                disabled={!isSkyGeniRecommendationEnabled} // Disabled if toggle is off
              >
                Account-Wise Recommendations
              </ToggleButton>
              <ToggleButton
                className={classes.toggleBtn}
                value="rankOrdered"
                disabled={!isSkyGeniRecommendationEnabled} // Disabled if toggle is off
              >
                Rank Ordered Recommendations
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Grid>

        {this.state.tab === "rankOrdered" ? (
          <Grid item xs={12} style={{ padding: "1rem", paddingLeft: 0 }}>
            {this.props.RankOrderedRecommendationsData !== "" ? (
              <Grid
                item
                container
                xs={12}
                justify="flex-start"
                style={{ marginTop: "1rem" }}
              >
                <Card style={{ padding: "1rem 2rem", width: "100%" }}>
                  <Grid container xs={12} justify="center">
                    <Grid item xs={1}></Grid>
                    <Grid item xs={10}>
                      <Typography
                        variant="h2"
                        className={classes.sectionTitle}
                        align="center"
                      >
                        Rank Ordered SkyGeni Recommendations
                      </Typography>
                    </Grid>
                    <Grid item xs={1} style={{ textAlign: "right" }}></Grid>
                  </Grid>

                  <Grid container xs={12} justify="center">
                    {(() => {
                      const rawColumns =
                        this.props.RankOrderedRecommendationsData.columns || [];
                      const rawData =
                        this.props.RankOrderedRecommendationsData.data || [];
                      const { columns, rows } =
                        transformToResizableFromSimpleColumns(
                          rawColumns,
                          rawData,
                          this.props.Currency_Symbol
                        );
                      const getCellValue = createGetCellValue(rawColumns);
                      return (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <ResizableTable
                            key="rankOrdered-table"
                            columns={columns}
                            rows={rows}
                            currencySymbol={this.props.Currency_Symbol}
                            getCellValue={getCellValue}
                            getCellStyle={getCellStyleCommon}
                            rowKey="_index"
                            heading={null}
                            maxWidth="100%"
                            hideSubHeaderRow={true}
                          />
                        </div>
                      );
                    })()}
                  </Grid>
                </Card>
              </Grid>
            ) : (
              <Grid
                item
                container
                component={Card}
                justify="center"
                style={{ marginTop: "1rem" }}
              >
                <Skeleton
                  animation="wave"
                  variant="rect"
                  width="80%"
                  height={200}
                  style={{ margin: "20px 0px" }}
                />
              </Grid>
            )}
          </Grid>
        ) : (
          <Grid item xs={12} style={{ padding: "1rem", paddingLeft: 0 }}>
            {this.state.tab === "summary" &&
            this.props.SummaryRecommendationsData !== "" ? (
              <Grid
                item
                container
                xs={12}
                justify="flex-start"
                style={{ marginTop: "1rem" }}
              >
                <Card style={{ padding: "1rem 2rem", width: "100%" }}>
                  {/* Summary KPI cards - Top Row */}
                  <Grid
                    container
                    spacing={2}
                    className={classes.kpiRow}
                    alignItems="stretch"
                  >
                    <Grid item xs={12} md={6}>
                      <Card
                        className={`${classes.kpiCard} ${classes.kpiNeutral}`}
                      >
                        <Typography variant="h5" className={classes.title}>
                          Current Installed Base Pipeline
                        </Typography>
                        <div className={classes.metricRow}>
                          <Typography
                            variant="h2"
                            className={classes.metricPrimary}
                          >
                            {formatMillions(basePipeline)}
                          </Typography>
                        </div>
                      </Card>
                    </Grid>

                    {/* TOGGLE LOGIC HERE: Show Switch OR Blue Card */}
                    <Grid item xs={12} md={6}>
                      {!isSkyGeniRecommendationEnabled ? (
                        // State 1: Disabled (Show Toggle)
                        <Card className={classes.toggleCard}>
                          <Switch
                            checked={isSkyGeniRecommendationEnabled}
                            onChange={this.handleToggleChange}
                            color="primary"
                            name="skygeniToggle"
                            inputProps={{ "aria-label": "enable skygeni" }}
                          />
                          <Typography className={classes.toggleLabel}>
                            Enable SkyGeni Recommendations
                          </Typography>
                        </Card>
                      ) : (
                        // State 2: Enabled (Show Blue Card)
                        <Card
                          className={`${classes.kpiCard} ${classes.kpiPrimary}`}
                        >
                          <Typography variant="h5" className={classes.title}>
                            SkyGeni Identified White Space Pipeline
                          </Typography>
                          <div className={classes.metricRow}>
                            <Typography
                              variant="h2"
                              className={classes.metricPrimary}
                            >
                              {formatMillions(sumListPriceUpdated)}
                            </Typography>
                            <Typography
                              variant="h5"
                              className={classes.upliftLabel}
                            >
                              +{formatPercent(upliftPercent)} Uplift
                            </Typography>
                          </div>
                        </Card>
                      )}
                    </Grid>
                  </Grid>

                  {/* Secondary KPI cards - Middle Row */}
                  <Grid container spacing={2} style={{ marginBottom: "1rem" }}>
                    <Grid item xs={12} md={4}>
                      <Card className={classes.smallKpiCard}>
                        <Typography
                          variant="subtitle1"
                          className={`${classes.title} ${classes.secondaryTitle}`}
                          style={{ height: "auto", minHeight: "unset" }}
                        >
                          Current Installed Base Pipeline
                        </Typography>
                        <Typography
                          variant="h4"
                          className={classes.metricSecondary}
                        >
                          {formatMillions(basePipeline)}
                        </Typography>
                      </Card>
                    </Grid>

                    {/* Middle Card - Blurred when disabled */}
                    <Grid
                      item
                      xs={12}
                      md={4}
                      className={
                        isSkyGeniRecommendationEnabled
                          ? classes.normalContent
                          : classes.blurredContent
                      }
                    >
                      <Card className={classes.smallKpiCard}>
                        <Typography
                          variant="subtitle1"
                          className={`${classes.title} ${classes.secondaryTitle}`}
                          style={{ height: "auto", minHeight: "unset" }}
                        >
                          SkyGeni Identified White Space Pipeline
                        </Typography>
                        <Typography
                          variant="h4"
                          className={classes.metricSecondary}
                        >
                          {formatMillions(sumListPriceUpdated)}
                        </Typography>
                      </Card>
                    </Grid>

                    {/* Right Card - Blurred when disabled */}
                    <Grid
                      item
                      xs={12}
                      md={4}
                      className={
                        isSkyGeniRecommendationEnabled
                          ? classes.normalContent
                          : classes.blurredContent
                      }
                    >
                      <Card className={classes.smallKpiCard}>
                        <Typography
                          variant="subtitle1"
                          className={`${classes.title} ${classes.secondaryTitle}`}
                          style={{ height: "auto", minHeight: "unset" }}
                        >
                          Incremental Hidden Expansion Pipeline
                        </Typography>
                        <Typography
                          variant="h4"
                          className={classes.metricSecondary}
                        >
                          {formatMillions(incrementalHiddenExpansion)}
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Table Section - Blurred when disabled */}
                  <div
                    className={
                      isSkyGeniRecommendationEnabled
                        ? classes.normalContent
                        : classes.blurredContent
                    }
                  >
                    <Grid container xs={12} justify="center">
                      <Grid item xs={1}></Grid>
                      <Grid item xs={10}>
                        <Typography
                          variant="h2"
                          className={classes.sectionTitle}
                          align="center"
                        >
                          Top 5 Prescriptive Opportunities
                        </Typography>
                      </Grid>
                      <Grid item xs={1} style={{ textAlign: "right" }}></Grid>
                    </Grid>
                    <Grid container xs={12} justify="center">
                      {(() => {
                        const rawColumns =
                          this.props.SummaryRecommendationsData.columns || [];
                        const rawData =
                          this.props.SummaryRecommendationsData.data || [];
                        const { columns, rows } =
                          transformToResizableFromSimpleColumns(
                            rawColumns,
                            rawData,
                            this.props.Currency_Symbol
                          );
                        const getCellValue = createGetCellValue(rawColumns);
                        return (
                          <div
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <ResizableTable
                              key="summary-table"
                              columns={columns}
                              rows={rows}
                              currencySymbol={this.props.Currency_Symbol}
                              getCellValue={getCellValue}
                              getCellStyle={getCellStyleCommon}
                              rowKey="_index"
                              heading={null}
                              maxWidth="100%"
                              hideSubHeaderRow={true}
                            />
                          </div>
                        );
                      })()}
                    </Grid>
                  </div>
                </Card>
              </Grid>
            ) : this.state.tab === "accountWise" &&
              this.props.AccountWiseRecommendationsData !== "" ? (
              <Grid
                item
                container
                xs={12}
                justify="flex-start"
                style={{ marginTop: "1rem" }}
              >
                <Card style={{ padding: "1rem 2rem", width: "100%" }}>
                  <Grid container xs={12} justify="center">
                    <Grid item xs={1}></Grid>
                    <Grid item xs={10}>
                      <Typography
                        variant="h2"
                        style={{
                          width: "100%",
                          margin: "auto",
                          padding: "1rem",
                          paddingTop: "0.3rem",
                          fontWeight: 600,
                        }}
                        align="center"
                      >
                        Account-Wise SkyGeni Recommendations
                      </Typography>
                    </Grid>
                    <Grid item xs={1} style={{ textAlign: "right" }}></Grid>
                  </Grid>
                  <Grid container xs={12} justify="center">
                    {(() => {
                      const rawColumns =
                        this.props.AccountWiseRecommendationsData.columns || [];
                      const rawData =
                        this.props.AccountWiseRecommendationsData.data || [];
                      const { columns, rows } =
                        transformToResizableFromSimpleColumns(
                          rawColumns,
                          rawData,
                          this.props.Currency_Symbol
                        );
                      const getCellValue = createGetCellValue(rawColumns);
                      return (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <ResizableTable
                            key="accountWise-table"
                            columns={columns}
                            rows={rows}
                            currencySymbol={this.props.Currency_Symbol}
                            getCellValue={getCellValue}
                            getCellStyle={getCellStyleCommon}
                            rowKey="_index"
                            heading={null}
                            maxWidth="100%"
                            hideSubHeaderRow={true}
                          />
                        </div>
                      );
                    })()}
                  </Grid>
                </Card>
              </Grid>
            ) : (
              <Grid
                item
                container
                component={Card}
                justify="center"
                style={{ marginTop: "1rem" }}
              >
                <Skeleton
                  animation="wave"
                  variant="rect"
                  width="80%"
                  height={200}
                  style={{ margin: "20px 0px" }}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    );
  }
}

const mapStateToProps = (state) => {
  var {
    RankOrderedRecommendationsData,
    SummaryRecommendationsData,
    AccountWiseRecommendationsData,
    user,
  } = state.app;

  return {
    Currency_Symbol: user.Currency,
    RankOrderedRecommendationsData,
    SummaryRecommendationsData,
    AccountWiseRecommendationsData,
    helperTextCharts: user.elements.flatMap((item) => item.charts || []),
  };
};

const mapDispatchToProps = (dispatch) => ({
  getRankOrderedRecommendationsData: () => {
    dispatch({ type: "get_RankOrderedRecommendations_data_request" });
    appService.genericWhiteSpaceIgniteData().then(
      (json) => {
        dispatch({ type: "get_RankOrderedRecommendations_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_RankOrderedRecommendations_data_failure",
            error: "Something went wrong",
          });
        else
          dispatch({
            type: "get_RankOrderedRecommendations_data_failure",
            error,
          });
      }
    );
  },
  getSummaryRecommendationsData: () => {
    dispatch({ type: "get_SummaryRecommendations_data_request" });
    appService.genericWhiteSpaceIgniteSummaryData().then(
      (json) => {
        dispatch({ type: "get_SummaryRecommendations_data_success", json });
      },
      (error) => {
        dispatch({
          type: "get_SummaryRecommendations_data_failure",
          error: "Something went wrong",
        });
      }
    );
  },
  getAccountWiseRecommendationsData: () => {
    dispatch({ type: "get_AccountWiseRecommendations_data_request" });
    appService.genericWhiteSpaceIgniteAccountWiseData().then(
      (json) => {
        dispatch({ type: "get_AccountWiseRecommendations_data_success", json });
      },
      (error) => {
        dispatch({
          type: "get_AccountWiseRecommendations_data_failure",
          error: "Something went wrong",
        });
      }
    );
  },
});

const connectedWhiteSpaceConversionEngine = connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(WhiteSpaceConversionEngine));
export default connectedWhiteSpaceConversionEngine;
